import { Button } from "@/components/ui/button";
import { Download, Printer } from "lucide-react";
import { format } from "date-fns";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

interface Props {
  item: any;
}

function escapeHtml(unsafe: string): string {
  return (unsafe ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

function stripHtml(html?: string | null) {
  if (!html) return "";
  const d = document.createElement("div");
  d.innerHTML = html;
  return d.textContent || d.innerText || "";
}

export default function ResearchExportButtons({ item }: Props) {
  const downloadMarkdown = () => {
    const lines: string[] = [];
    lines.push(`# ${item.title}`);
    lines.push("");
    if (item.published_at) lines.push(`_Published ${format(new Date(item.published_at), "dd/MM/yyyy")}_`);
    if (item.summary) { lines.push(""); lines.push(item.summary); }
    if (item.ai_house_view_draft) {
      lines.push(""); lines.push("## House View"); lines.push("");
      lines.push(item.ai_house_view_draft);
    }
    if (item.body_markdown || item.body_html) {
      lines.push(""); lines.push("## Content"); lines.push("");
      lines.push(item.body_markdown || stripHtml(item.body_html));
    }
    if (item.ai_risks) {
      lines.push(""); lines.push("## Key Risks"); lines.push("");
      lines.push(item.ai_risks);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(item.slug || item.title || "research").toString().replace(/[^a-z0-9-_]+/gi, "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPdf = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(`
      <html><head><title>${escapeHtml(item.title)}</title>
      <style>
        body { font-family: -apple-system, system-ui, sans-serif; max-width: 720px; margin: 40px auto; padding: 0 24px; color: #111; }
        h1 { color: #0A2351; border-bottom: 2px solid #004742; padding-bottom: 8px; }
        h2 { color: #0A2351; margin-top: 24px; }
        .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
        .house-view { background: #f0f7f5; border-left: 4px solid #004742; padding: 12px 16px; margin: 16px 0; }
        .risks { background: #fff7f0; border-left: 4px solid #c2410c; padding: 12px 16px; margin: 16px 0; }
        @media print { body { margin: 0; } }
      </style></head><body>
      <h1>${escapeHtml(item.title)}</h1>
      <div class="meta">${item.published_at ? `Published ${format(new Date(item.published_at), "dd/MM/yyyy")}` : ""}
        ${item.primary_axis ? ` · ${escapeHtml(item.primary_axis)}` : ""}
        ${item.portfolio_level ? ` · P${item.portfolio_level}` : ""}
        ${item.conviction ? ` · Conviction ${item.conviction}/5` : ""}
      </div>
      ${item.summary ? `<p>${escapeHtml(item.summary)}</p>` : ""}
      ${item.ai_house_view_draft ? `<div class="house-view"><strong>House View</strong><br/>${escapeHtml(item.ai_house_view_draft).replace(/\n/g, "<br/>")}</div>` : ""}
      ${item.body_html ? sanitizeHtml(item.body_html) : `<p>${escapeHtml(item.body_markdown || "").replace(/\n/g, "<br/>")}</p>`}
      ${item.ai_risks ? `<div class="risks"><strong>Key Risks</strong><br/>${escapeHtml(item.ai_risks).replace(/\n/g, "<br/>")}</div>` : ""}
      </body></html>
    `);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="flex flex-col gap-2">
      <Button variant="outline" size="sm" onClick={printPdf}>
        <Printer className="h-4 w-4 mr-1" /> Print / PDF
      </Button>
      <Button variant="outline" size="sm" onClick={downloadMarkdown}>
        <Download className="h-4 w-4 mr-1" /> Markdown
      </Button>
    </div>
  );
}
